"use client";

import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * The night sky behind the landing page's dark bands.
 *
 * Three layers that move independently, so the band never looks like a
 * single animated image:
 *
 *   1. an aurora of four blurred colour blobs on a diagonal wash
 *   2. a fine grid, masked to the centre, for scale
 *   3. drifting dust motes
 *
 * Every layer stops under `prefers-reduced-motion`. The blobs simply
 * hold their first frame and the dust is not rendered at all — the
 * band still reads as night, just as still.
 */

interface AuroraStageProps {
  className?: string;
  /** Adds a fade to the page background at the bottom edge. */
  fadeBottom?: boolean;
}

/** Deterministic mote layout — no Math.random, so the band is identical
 *  on every render. */
const DUST = Array.from({ length: 16 }, (_, i) => ({
  left: `${(i * 37 + 11) % 96}%`,
  top: `${(i * 53 + 7) % 94}%`,
  delay: `${(i % 7) * 1.7}s`,
  duration: `${20 + (i % 5) * 7}s`,
  size: i % 3 === 0 ? 2 : 1,
}));

export function AuroraStage({
  className,
  fadeBottom = false,
}: AuroraStageProps) {
  const reduceMotion = useReducedMotion();

  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden bg-[hsl(224_45%_7%)]",
        className,
      )}
    >
      {/* Base wash — a slow diagonal, so the band is never one flat colour. */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_18%_0%,hsl(217_62%_22%)_0%,transparent_58%),radial-gradient(100%_80%_at_92%_100%,hsl(38_58%_17%)_0%,transparent_52%)]" />

      <div
        className={cn(
          "absolute -left-32 -top-28 h-[440px] w-[440px] rounded-full bg-primary/35 blur-[120px] animate-mesh-1",
          reduceMotion && "opacity-60",
        )}
      />
      <div
        className={cn(
          "absolute -bottom-40 -right-24 h-[520px] w-[520px] rounded-full bg-secondary/25 blur-[130px] animate-mesh-2",
          reduceMotion && "opacity-60",
        )}
      />
      <div
        className={cn(
          "absolute left-1/2 top-[38%] h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-blue-400/15 blur-[110px] animate-mesh-3",
          reduceMotion && "opacity-70",
        )}
      />
      <div
        className={cn(
          "absolute left-[6%] top-[64%] h-[380px] w-[380px] rounded-full bg-amber-500/10 blur-[120px] animate-mesh-4",
          reduceMotion && "opacity-70",
        )}
      />

      {/* Grid — masked to the centre so the edges stay soft. */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(0 0% 100% / 0.045) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.045) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(70% 60% at 50% 40%, black 0%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(70% 60% at 50% 40%, black 0%, transparent 100%)",
        }}
      />

      {!reduceMotion && (
        <div className="absolute inset-0 overflow-hidden">
          {DUST.map((mote, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-white/25 animate-particle"
              style={{
                left: mote.left,
                top: mote.top,
                width: mote.size,
                height: mote.size,
                animationDelay: mote.delay,
                animationDuration: mote.duration,
              }}
            />
          ))}
        </div>
      )}

      {/* Vignette — keeps the eye on the headline, hides the blob edges. */}
      <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_45%_40%,transparent_0%,hsl(224_45%_7%/_0.55)_100%)]" />

      {/* Hand off to the light page below without a hard cut. */}
      {fadeBottom && (
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-background" />
      )}
    </div>
  );
}
